import React, { createContext, useContext, useState, useCallback } from 'react';
import { IdentifierType } from './ClickableIdentifier';
import { TraceabilityModal } from './TraceabilityModal';

interface TraceabilityState {
  isOpen: boolean;
  type: IdentifierType | null;
  value: string | null;
}

interface TraceabilityContextValue {
  openTraceability: (type: IdentifierType, value: string) => void;
}

const TraceabilityContext = createContext<TraceabilityContextValue | null>(null);

export const useTraceability = (): TraceabilityContextValue => {
  const context = useContext(TraceabilityContext);
  if (!context) {
    throw new Error('useTraceability must be used within a TraceabilityProvider');
  }
  return context;
};

export const TraceabilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<TraceabilityState>({
    isOpen: false,
    type: null,
    value: null
  });

  const openTraceability = useCallback((type: IdentifierType, value: string) => {
    setState({ isOpen: true, type, value });
  }, []);

  const closeTraceability = useCallback(() => {
    setState({ isOpen: false, type: null, value: null });
  }, []);

  return (
    <TraceabilityContext.Provider value={{ openTraceability }}>
      {children}
      {state.isOpen && state.type && state.value && (
        <TraceabilityModal
          type={state.type}
          value={state.value}
          onClose={closeTraceability}
          onNavigate={openTraceability}
        />
      )}
    </TraceabilityContext.Provider>
  );
};
